import { useState } from "react";
import { MapPin, Mail, Clock, Loader2, ArrowRight, Phone } from "lucide-react";
import { toast } from "sonner";
import { useLanguage } from "@/context/LanguageContext";
import { api } from "@/lib/api";

const inputCls =
  "w-full rounded-md border border-line bg-white px-3.5 py-3 text-sm text-brandink outline-none transition-colors duration-200 placeholder:text-muted/70 focus:border-link";

const InfoRow = ({ icon: Icon, children, testid }) => (
  <p className="flex items-center gap-3 text-sm text-brandink/90" data-testid={testid}>
    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-brand text-brandink">
      <Icon className="h-4 w-4" />
    </span>
    {children}
  </p>
);

export default function Contact() {
  const { t } = useLanguage();
  const [form, setForm] = useState({ name: "", email: "", company: "", message: "" });
  const [status, setStatus] = useState("idle");

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const valid = form.name && /\S+@\S+\.\S+/.test(form.email) && form.message;

  const submit = async (e) => {
    e.preventDefault();
    if (!valid || status === "sending") return;
    setStatus("sending");
    try {
      await api.post("/contact", form);
      setStatus("sent");
      toast.success(t("contact_success"));
      setForm({ name: "", email: "", company: "", message: "" });
    } catch (err) {
      setStatus("idle");
      toast.error(t("contact_error"));
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="px-4 py-10 lg:px-8">
      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-10 rounded-lg border border-line bg-white p-6 md:p-10 lg:grid-cols-2">
        <div>
          <p className="mb-2 text-xs font-bold uppercase tracking-wide text-link">{t("contact_label")}</p>
          <h2 className="text-3xl font-extrabold tracking-tight text-brandink">
            {t("contact_title_1")} {t("contact_title_2")}
          </h2>
          <p className="mt-4 max-w-md text-sm leading-relaxed text-muted">
            {t("contact_sub")}
          </p>
          <div className="mt-8 space-y-4">
            <InfoRow icon={MapPin}>{t("contact_address")}</InfoRow>
            <InfoRow icon={Phone} testid="contact-phone-line">
              <span dir="ltr">{t("contact_phone")}</span>
            </InfoRow>
            <InfoRow icon={Mail} testid="contact-email-line">
              <a href="mailto:cambroogt1@gmail.com" className="transition-colors duration-200 hover:text-link">
                cambroogt1@gmail.com
              </a>
            </InfoRow>
            <InfoRow icon={Clock}>{t("contact_hours")}</InfoRow>
          </div>
        </div>

        <form data-testid="contact-form" onSubmit={submit} className="space-y-4 self-center">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <input data-testid="contact-name-input" className={inputCls} placeholder={t("contact_name")} value={form.name} onChange={set("name")} />
            <input data-testid="contact-email-input" className={inputCls} placeholder={t("contact_email")} type="email" value={form.email} onChange={set("email")} />
          </div>
          <input data-testid="contact-company-input" className={inputCls} placeholder={t("contact_company")} value={form.company} onChange={set("company")} />
          <textarea
            data-testid="contact-message-input"
            className={`${inputCls} min-h-[120px] resize-none`}
            placeholder={t("contact_message")}
            value={form.message}
            onChange={set("message")}
          />
          <button
            data-testid="contact-submit-button"
            type="submit"
            disabled={!valid || status === "sending"}
            className="group flex items-center gap-2 rounded-md bg-brand px-8 py-3 text-sm font-bold text-brandink transition-colors duration-200 hover:bg-branddark disabled:cursor-not-allowed disabled:opacity-40"
          >
            {status === "sending" ? (
              <>
                {t("contact_sending")} <Loader2 className="h-4 w-4 animate-spin" />
              </>
            ) : (
              <>
                {t("contact_submit")}
                <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1 rtl:-scale-x-100 rtl:group-hover:-translate-x-1" />
              </>
            )}
          </button>
        </form>
      </div>
    </section>
  );
}
